import CHOICES from "CHOICES";
import { mutate } from "utils/relay";
import {
  AUTH_TOKEN,
  ENV_MAPPER,
  REFRESH_TOKEN,
  SELECTED_ORGANIZATION,
} from "utils/constants";
import {
  decodeReadStringifyObj,
  emptyProxyObject,
  getEnv,
  getSessionStorage,
  ProxyObjectType,
} from "utils/utils";
import { Action, Thunk, Computed, thunk, action, computed } from "easy-peasy";
import { Cookies } from "react-cookie";
import moment from "moment";
import { setAuthRefreshHandler } from "api/client";
const UK = CHOICES.UserKind;
const cookie = new Cookies();
const env = getEnv();

const cookieDomain = (currentEnv: string) =>
  currentEnv === ENV_MAPPER.production
    ? ".v3.nuflights.com"
    : currentEnv === ENV_MAPPER.connect
      ? ".connect.nuflights.com"
      : currentEnv === ENV_MAPPER.development
        ? `.dev.nuflights.com`
        : `.staging.llc.nuflights.com`;

export interface CurrentUserModel {
  username: string;
  email: string;
}
export interface CorporateSubAccountList {}

export interface SelectedOrganization {
  group?: string;
  group_name?: string;
  is_direct_member?: boolean;
  roles?: string[];
  permissions?: string[];
  [key: string]: any;
}

export interface AuthModel {
  currentUser: CurrentUserModel | ProxyObjectType;
  authToken: string | null;
  isLoggedIn: Computed<AuthModel, boolean>;
  isAdmin: Computed<AuthModel, boolean>;
  setAuthToken: Action<AuthModel, string | null>;
  setCurrentUser: Action<AuthModel, CurrentUserModel | ProxyObjectType>;
  updateAuthToken: Thunk<AuthModel, any>;
  clearAuthToken: Thunk<AuthModel>;
  fetchAuthToken: Thunk<AuthModel, any>;
  initRefreshToken: Thunk<AuthModel, boolean>;
  permissions: any;
  setPermissions: Action<AuthModel, any>;
  updatePermissions: Thunk<AuthModel, string[] | string | undefined>;
  refreshToken: string | null;
  selectedOrganization: SelectedOrganization | null;
  setSelectedOrganization: Action<AuthModel, SelectedOrganization | null>;
  setRefreshToken: Action<AuthModel, string | null>;
  updateRefreshToken: Thunk<AuthModel, any>;
  clearRefreshToken: Thunk<AuthModel>;
  setCoporateSubAccount: Action<AuthModel, any[] | ProxyObjectType>;
  coporateSubAccount: any;
  corporateList: any;
  setCorporateList: Action<AuthModel, any[]>;
}

let refreshIntervalId: number | null = null;

const AuthStore: AuthModel = {
  currentUser: emptyProxyObject,
  coporateSubAccount: null,
  corporateList: null,
  authToken: getSessionStorage(AUTH_TOKEN),
  permissions: {},
  refreshToken: cookie.get(REFRESH_TOKEN) ?? null,
  selectedOrganization: decodeReadStringifyObj(SELECTED_ORGANIZATION) ?? null,

  isLoggedIn: computed((state) => Boolean(state.authToken)),
  isAdmin: computed((state: any) => state.currentUser.kind === UK.ADMIN),

  setAuthToken: action((state: any, authToken) => {
    state.authToken = authToken;
  }),

  setRefreshToken: action((state: any, refreshToken) => {
    state.refreshToken = refreshToken;
  }),

  setSelectedOrganization: action((state: any, selectedOrganization) => {
    state.selectedOrganization = selectedOrganization;
  }),

  setCurrentUser: action((state: any, currentUser) => {
    state.currentUser = currentUser;
  }),

  setPermissions: action((state: any, permissions) => {
    state.permissions = permissions;
  }),
  setCoporateSubAccount: action((state: any, item) => {
    state.coporateSubAccount = item;
  }),
  setCorporateList: action((state: any, item) => {
    state.corporateList = item;
  }),
  updatePermissions: thunk((actions, permissions) => {
    const perms: Record<string, boolean> = {};
    const permissionList = Array.isArray(permissions)
      ? permissions
      : permissions
        ? [permissions]
        : [];
    permissionList.forEach((p: string) => {
      perms[p] = true;
    });
    actions.setPermissions(perms);
  }),

  updateAuthToken: thunk((actions, authToken) => {
    window.sessionStorage.setItem(AUTH_TOKEN, JSON.stringify(authToken.self));
    actions.setAuthToken(authToken.self);
  }),

  updateRefreshToken: thunk((actions, refreshToken) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(REFRESH_TOKEN, refreshToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          expires:
            refreshToken.expiry instanceof Date
              ? refreshToken.expiry
              : new Date(refreshToken.expiry),
        })
      : cookie.set(REFRESH_TOKEN, refreshToken.self, {
          path: "/",
          sameSite: "strict",
          secure: true,
        });
    actions.setRefreshToken(refreshToken.self);
  }),

  clearAuthToken: thunk((actions) => {
    window.sessionStorage.removeItem(AUTH_TOKEN);
    actions.setAuthToken(null);
    actions.setCurrentUser(emptyProxyObject);
    actions.setPermissions({});
    if (env !== "local") {
      cookie.remove(SELECTED_ORGANIZATION, {
        domain: cookieDomain(env),
        path: "/",
        expires: new Date(0),
      });
    } else {
      cookie.remove(SELECTED_ORGANIZATION, { path: "/" });
    }
  }),

  clearRefreshToken: thunk((actions) => {
    if (env !== ENV_MAPPER.local) {
      cookie.remove(REFRESH_TOKEN, {
        domain: cookieDomain(env),
        path: "/",
      });
    } else {
      cookie.remove(REFRESH_TOKEN, { path: "/" });
    }
    actions.setRefreshToken(null);
    actions.setCurrentUser(emptyProxyObject);
  }),

  fetchAuthToken: thunk(
    (actions, { mutation, input, onSuccess, onFailure }) => {
      mutate({
        mutation,
        input: input,
        onSuccess: async (data: any) => {
          const unixTime = Number(
            moment.unix(data.login?.refreshExpiresIn || 0)
          );
          const expiry = new Date(unixTime);
          if (data.login) {
            // authToken should only be set via idpRefresh
            // await actions.updateAuthToken({ self: data.login.token, expiry });
            await actions.updateRefreshToken({
              self: data.login.refreshToken,
              expiry,
            });
          }
          onSuccess(data);
        },
        onFailure: function (messages: []) {
          actions.clearAuthToken();
          actions.clearRefreshToken();
          onFailure(messages);
        },
      });
    }
  ),

  initRefreshToken: thunk(() => {
    if (refreshIntervalId) clearInterval(refreshIntervalId);
    setAuthRefreshHandler(null);
    return Promise.resolve(false);
  }),
};

export default AuthStore;
